import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { PageContainer, Spinner, LoadingContainer } from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const TenantSelector = () => {
  const { t } = useTranslation('tenants');
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  useEffect(() => {
    // tenant_admin vai direto para o seu tenant
    if (!isSallusAdmin && user.tenantId) {
      navigate(`/dashboard/${user.tenantId}`, { replace: true });
      return;
    }
    // sallus_admin carrega lista de tenants
    api.get('/tenants')
      .then(r => setTenants(r.data.tenants || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = tenants.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.tenantId?.toLowerCase().includes(search.toLowerCase())
  );

  if (!isSallusAdmin) {
    return (
      <Fallback>
        <FallbackCard>
          <FallbackTitle>{t('tenantNoAccess')}</FallbackTitle>
          <FallbackSub>{t('tenantNoAccessMsg')}</FallbackSub>
        </FallbackCard>
      </Fallback>
    );
  }

  return (
    <>
      <AppHeader title={t('clients')} />
      <PageContainer>
        <Header>
          <Title>{t('clients')}</Title>
          <Sub>{t('selectClientDesc')}</Sub>
        </Header>

        <SearchBox>
          <Search size={16} color={theme.colors.textMuted} />
          <SearchInput
          placeholder={t('searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </SearchBox>

        {loading ? (
          <LoadingContainer><Spinner size="24px" /></LoadingContainer>
        ) : filtered.length === 0 ? (
          <Empty>{t('noClientFound')}</Empty>
        ) : (
          <Grid>
            {filtered.map(tenant => (
              <TenantCard key={tenant.tenantId} onClick={() => navigate(`/dashboard/${tenant.tenantId}`)}>
                <TenantIcon>🏢</TenantIcon>
                <TenantInfo>
                  <TenantName>{tenant.name}</TenantName>
                  <TenantMeta>{tenant.tenantId} · {tenant.tier || 'starter'}</TenantMeta>
                </TenantInfo>
                <StatusDot $active={tenant.status === 'active'} />
              </TenantCard>
            ))}
          </Grid>
        )}
      </PageContainer>
    </>
  );
};

const Header = styled.div`margin-bottom: ${theme.spacing[6]};`;
const Title = styled.h1`font-size: ${theme.typography['2xl']}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[1]} 0;`;
const Sub = styled.p`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; margin: 0;`;
const SearchBox = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.lg}; background: ${theme.colors.white}; margin-bottom: ${theme.spacing[5]}; max-width: 400px;`;
const SearchInput = styled.input`border: none; outline: none; font-size: ${theme.typography.sm}; flex: 1; color: ${theme.colors.textPrimary}; &::placeholder { color: ${theme.colors.textMuted}; }`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: ${theme.spacing[4]};`;
const TenantCard = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; padding: ${theme.spacing[4]}; background: ${theme.colors.white}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.xl}; cursor: pointer; transition: ${theme.transitions.base}; &:hover { border-color: ${theme.colors.primary}; box-shadow: ${theme.shadows.md}; transform: translateY(-2px); }`;
const TenantIcon = styled.div`font-size: 32px; flex-shrink: 0;`;
const TenantInfo = styled.div`flex: 1; min-width: 0;`;
const TenantName = styled.div`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const TenantMeta = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 2px;`;
const StatusDot = styled.div`width: 8px; height: 8px; border-radius: 50%; background: ${p => p.$active ? theme.colors.success : theme.colors.border}; flex-shrink: 0;`;
const Empty = styled.div`text-align: center; color: ${theme.colors.textMuted}; font-size: ${theme.typography.sm}; padding: ${theme.spacing[10]};`;
const Fallback = styled.div`min-height: 100vh; display: flex; align-items: center; justify-content: center; background: ${theme.colors.gray50};`;
const FallbackCard = styled.div`background: white; border-radius: ${theme.radii.xl}; padding: ${theme.spacing[8]}; max-width: 400px; text-align: center; box-shadow: ${theme.shadows.lg};`;
const FallbackTitle = styled.h2`font-size: ${theme.typography.xl}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[2]} 0;`;
const FallbackSub = styled.p`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; margin: 0;`;

export default TenantSelector;
